export function detectType(v: unknown): 'boolean' | 'number' | 'string' | 'json' {
  if (typeof v === 'boolean') return 'boolean';
  if (typeof v === 'number') return 'number';
  if (typeof v === 'string') return 'string';
  return 'json';
}

export function stringifyJSON(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return '';
  }
}

export function parseJSONSafe(s: string): { ok: true; value: unknown } | { ok: false; error: string } {
  try {
    return { ok: true as const, value: JSON.parse(s) };
  } catch (e) {
    return { ok: false as const, error: e instanceof Error ? e.message : 'Invalid JSON' };
  }
}

export function generateCurlCommand(request: {
  method?: string;
  url?: string;
  requestHeaders?: Record<string, string>;
  requestData?: unknown;
}): string {
  const method = request.method || 'GET';
  const url = request.url || '';

  let curl = `curl -X ${method.toUpperCase()} "${url}"`;

  // Add headers if available
  if (request.requestHeaders && Object.keys(request.requestHeaders).length > 0) {
    Object.entries(request.requestHeaders).forEach(([key, value]) => {
      curl += ` \\\n  -H "${key}: ${value}"`;
    });
  }

  // Add request body if available
  if (request.requestData && method.toUpperCase() !== 'GET') {
    if (typeof request.requestData === 'string') {
      curl += ` \\\n  -d '${request.requestData}'`;
    } else {
      curl += ` \\\n  -d '${JSON.stringify(request.requestData)}'`;
    }
  }

  return curl;
}

export function formatJSONValue(value: unknown, depth: number): JSX.Element {
  if (value === null) {
    return <span className="text-12 text-purple-9">null</span>;
  }

  if (typeof value === 'boolean') {
    return <span className="text-12 text-blue-9">{String(value)}</span>;
  }

  if (typeof value === 'number') {
    return <span className="text-12 text-green-9">{String(value)}</span>;
  }

  if (typeof value === 'string') {
    return <span className="text-12 text-orange-9">&ldquo;{value}&rdquo;</span>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-12 text-grey-12">[]</span>;
    }

    return (
      <div>
        <span className="text-12 text-grey-12">[</span>
        {value.map((item, index) => (
          <div key={index} className="ml-6">
            {formatJSONValue(item, depth + 1)}
            {index < value.length - 1 && <span className="text-12 text-grey-12">,</span>}
          </div>
        ))}
        <span className="text-12 text-grey-12">]</span>
      </div>
    );
  }

  if (typeof value === 'object' && value !== null) {
    const objectValue = value as Record<string, unknown>;
    const keys = Object.keys(objectValue);
    if (keys.length === 0) {
      return <span className="text-12 text-grey-12">{'{}'}</span>;
    }

    return (
      <div>
        <span className="text-12 text-grey-12">{'{'}</span>
        {keys.map((key, index) => (
          <div key={key} className="ml-6">
            <span className="text-12 text-blue-10">&ldquo;{key}&rdquo;</span>
            <span className="text-12 text-grey-12">: </span>
            {formatJSONValue(objectValue[key], depth + 1)}
            {index < keys.length - 1 && <span className="text-12 text-grey-12">,</span>}
          </div>
        ))}
        <span className="text-12 text-grey-12">{'}'}</span>
      </div>
    );
  }

  return <span className="text-12 text-grey-12">{String(value)}</span>;
}

// Function to format JSON with syntax highlighting
export function formatJSON(data: unknown): JSX.Element {
  if (typeof data === 'string') {
    try {
      // Try to parse and re-stringify for consistent formatting
      const parsed = JSON.parse(data);
      return formatJSONValue(parsed, 0);
    } catch {
      // If it's not valid JSON, return as-is
      return <span className="text-grey-12">{data}</span>;
    }
  }

  if (data === null) return <span className="text-purple-9">null</span>;
  if (data === undefined) return <span className="text-grey-10">undefined</span>;

  try {
    return formatJSONValue(data, 0);
  } catch {
    return <span className="text-grey-12">{String(data)}</span>;
  }
}
